import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { Email, ArrowBack, Send, LockReset } from "@mui/icons-material";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { AtomicLogo } from "../../../assets/AtomicLogo";

interface ForgotPasswordProps {
  onSwitchToSignIn: () => void;
}

export const ForgotPasswordComponent = ({
  onSwitchToSignIn,
}: ForgotPasswordProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await resetPassword({ username: email });
      setNeedsVerification(true);
      setSuccess("Password reset code sent to your email");
    } catch (err: any) {
      console.error("Reset password error:", err);

      if (err.name === "UserNotFoundException") {
        setError("No account found with this email address.");
      } else if (err.name === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setVerifying(false);
      return;
    }

    // Basic password validation
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      setVerifying(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: newPassword,
      });
      setSuccess(
        "Password reset successfully! You can now sign in with your new password."
      );
      setTimeout(() => {
        onSwitchToSignIn();
      }, 2000);
    } catch (err: any) {
      console.error("Confirm reset password error:", err);

      if (err.name === "CodeMismatchException") {
        setError(
          "Invalid verification code. Please check your email and try again."
        );
      } else if (err.name === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new one.");
      } else if (err.name === "InvalidPasswordException") {
        setError("Password does not meet requirements.");
      } else {
        setError(
          err.message ||
            "An error occurred during verification. Please try again."
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  if (needsVerification) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#fafafa",
          padding: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            width: "100%",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <CardContent sx={{ padding: 4 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <AtomicLogo width={64} height={64} />
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the verification code and your new password
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleConfirmResetPassword}>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                margin="normal"
                placeholder="Enter the 6-digit code"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={
                  verifying ||
                  !verificationCode ||
                  !newPassword ||
                  !confirmPassword
                }
                startIcon={
                  verifying ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <LockReset />
                  )
                }
                sx={{
                  mb: 2,
                  py: 1.5,
                  backgroundColor: "#3877F7",
                  "&:hover": {
                    backgroundColor: "#2d5fd6",
                  },
                }}
              >
                {verifying ? "Resetting..." : "Reset Password"}
              </Button>

              <Box sx={{ textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setNeedsVerification(false)}
                  sx={{ textDecoration: "none", fontWeight: "medium" }}
                >
                  <ArrowBack sx={{ verticalAlign: "middle", mr: 1 }} />
                  Back to email
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#fafafa",
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 400,
          width: "100%",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <AtomicLogo width={64} height={64} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Forgot Password?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your email address and we'll send you a code to reset your
              password
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleResetPassword}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !email}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Send />
                )
              }
              sx={{
                mb: 2,
                py: 1.5,
                backgroundColor: "#3877F7",
                "&:hover": {
                  backgroundColor: "#2d5fd6",
                },
              }}
            >
              {loading ? "Sending..." : "Send Reset Code"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component="button"
                type="button"
                onClick={onSwitchToSignIn}
                sx={{ textDecoration: "none", fontWeight: "medium" }}
              >
                <ArrowBack sx={{ verticalAlign: "middle", mr: 1 }} />
                Back to sign in
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
